import { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useDash } from "@/components/dashboard/Layout";
import { useAuth } from "@/components/site/auth";
import { PageHead, Card, StatusPill } from "@/components/dashboard/kit";
import { inputCls } from "@/components/site/AuthShell";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { api, formatApiError } from "@/lib/api";
import { inr } from "@/data/content";

const Field = ({ label, children }) => <label className="block"><span className="text-xs text-[var(--muted)] font-semibold">{label}</span><div className="mt-1">{children}</div></label>;

export function Profile() {
  const { user } = useDash();
  const { refreshMe } = useAuth();
  const keys = ["name", "phone", "upi_id", "account_holder", "bank_name", "account_number", "ifsc"];
  const [f, setF] = useState(Object.fromEntries(keys.map((k) => [k, user[k] || ""])));
  const [busy, setBusy] = useState(false);
  const inp = (k, ph) => <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className={inputCls} data-testid={`profile-${k}-input`} />;
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.patch("/me/profile", f); await refreshMe(); toast.success("Profile saved"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="profile-page">
      <PageHead title="Profile &" highlight="Payout" />
      <form onSubmit={save} className="grid lg:grid-cols-2 gap-6">
        <Card className="space-y-4">
          <h2 className="font-display text-lg font-semibold">Personal details</h2>
          <Field label="Full name">{inp("name")}</Field>
          <Field label="Phone / WhatsApp">{inp("phone")}</Field>
          <Field label="Email"><input disabled value={user.email} className={`${inputCls} opacity-60`} /></Field>
          <Field label="Your ID"><input disabled value={user.referral_code} className={`${inputCls} opacity-60`} /></Field>
        </Card>
        <Card className="space-y-4">
          <h2 className="font-display text-lg font-semibold">Payout details</h2>
          <Field label="UPI ID">{inp("upi_id", "name@upi")}</Field>
          <Field label="Account holder name">{inp("account_holder")}</Field>
          <Field label="Bank name">{inp("bank_name")}</Field>
          <div className="grid grid-cols-2 gap-3"><Field label="Account number">{inp("account_number")}</Field><Field label="IFSC">{inp("ifsc")}</Field></div>
        </Card>
        <div className="lg:col-span-2"><button disabled={busy} data-testid="profile-save-btn" className="btn-neon">{busy && <Loader2 className="animate-spin" size={16} />} Save changes</button></div>
      </form>
    </div>
  );
}

export function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try { await api.post("/me/password", { current_password: f.current_password, new_password: f.new_password }); toast.success("Password updated"); setF({ current_password: "", new_password: "", confirm: "" }); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const inp = (k, ph) => <input required type="password" minLength={k === "current_password" ? 1 : 6} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className={inputCls} data-testid={`password-${k}-input`} />;
  return (
    <div data-testid="password-page">
      <PageHead title="Change" highlight="Password" />
      <Card className="max-w-lg">
        <form onSubmit={save} className="space-y-3">
          {inp("current_password", "Current password")}{inp("new_password", "New password (min 6)")}{inp("confirm", "Confirm new password")}
          <button disabled={busy} data-testid="password-save-btn" className="btn-neon">{busy && <Loader2 className="animate-spin" size={16} />} Update password</button>
        </form>
      </Card>
    </div>
  );
}

export function Billing() {
  const { user, pkg } = useDash();
  return (
    <div data-testid="billing-page">
      <PageHead title="Billing &" highlight="Payments" sub="Payments are confirmed by our team. Online payment is coming soon." />
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead><tr className="text-left text-xs text-[var(--muted)]"><th className="pb-3">Package</th><th>Amount</th><th>Created</th><th>Status</th></tr></thead>
          <tbody><tr data-testid="billing-row" className="border-t border-[var(--surface-border)]">
            <td className="py-4 font-semibold">{pkg?.name || "—"}</td><td className="font-bold text-[var(--pink)]">{pkg ? inr(pkg.price) : "—"}</td>
            <td className="text-xs">{new Date(user.created_at).toLocaleDateString("en-IN")}</td>
            <td>{user.status === "approved" ? <StatusPill status="paid" /> : <StatusPill status={user.status} />}</td>
          </tr></tbody>
        </table>
      </Card>
    </div>
  );
}

export function Support() {
  const { user } = useDash();
  const [sent, setSent] = useState(false);
  return (
    <div data-testid="support-page">
      <PageHead title="Help &" highlight="Support" sub="Course doubt, payment or partner question? Write to us and a mentor will reply within a day." />
      <Card className="max-w-xl">
        {sent ? <div data-testid="support-success" className="text-center py-8"><CheckCircle2 className="mx-auto text-[var(--pink)]" size={44} /><p className="font-semibold mt-3">Request sent. We'll get back to you soon.</p><button onClick={() => setSent(false)} className="btn-ghost mt-5 text-sm">New request</button></div>
          : <EnquiryForm kind="support" item={`ID ${user.referral_code}`} prefix="support" onDone={() => setSent(true)} />}
      </Card>
    </div>
  );
}
